

































































































































































import { Component, Prop, Vue, Watch, Inject } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { saveAs } from "file-saver";
import { create } from "vue-modal-dialogs";

import ActionButton from "@/lib/components/ActionButton.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import ListItemPageNavigation from "@/lib/components/ListItemPageNavigation.vue";
import TrackingNumber from "@/lib/components/TrackingNumber.vue";
import EditableText from "@/modules/plushie/components/EditableText.vue";

import dataStore from "@/store";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import FactoryStore from "@/modules/factory/store";
import FileStorageStore from "@/modules/file-storage/store";
import ImageHandlerService from "@/modules/file-storage/image-handler.service";
import ResponseFileNameResolver from "@/modules/general/response-file-name-resolver.service";
import FactoryInvoiceStore from "@/modules/factory-invoice/store";
import FactoryInvoice from "@/modules/factory-invoice/factory-invoice.model";

import PublishAction from "./BulkShipmentViewPage/PublishAction.vue";
import ItemsList from "./BulkShipmentViewPage/ItemsList.vue";
import BulkShipmentBoxInfo from "./BulkShipmentBoxInfo.vue";
import CreateShippingManifestDialog from "./CreateShippingManifestDialog.vue";

import BulkShipmentExportsRepository from "../bulk-shipment-exports.respository";
import BulkShipmentRepository from "../bulk-shipment.repository";
import ShippingManifestExportsRepository from "../shipping-manifest-exports.repository";
import BulkShipment from "../bulk-shipment.model";
import BulkShipmentStore from "../store/index";
import { BulkShipmentStatusValue } from "../bulk-shipment-status.value";
import DistributionCenter from "../distribution-center.model";

@Component({
  metaInfo() {
    const component = this as BulkShipmentViewPage;

    return {
      title: component.bulkShipmentDisplayId
        ? "Bulk Shipment #" + component.bulkShipmentDisplayId
        : "Bulk Shipment",
    };
  },
  components: {
    ActionButton,
    BulkShipmentBoxInfo,
    EditableText,
    ItemsList,
    LoadingSpinner,
    ListItemPageNavigation,
    PublishAction,
    TrackingNumber,
  },
})
export default class BulkShipmentViewPage extends Vue {
  @Prop({ required: true })
  public readonly bulkShipmentId!: string;

  @Prop()
  public readonly query!: Record<string, any>;

  @Prop()
  public readonly listQuery?: Record<string, any>;

  @Inject("AuthenticatedUserProvider")
  private fAuthenticatedUserProvider!: AuthenticatedUserProvider;

  @Inject("BulkShipmentExportsRepository")
  private fBulkShipmentExportsRepository!: BulkShipmentExportsRepository;

  @Inject("ShippingManifestExportsRepository")
  private fShippingManifestExportsRepository!: ShippingManifestExportsRepository;

  @Inject("BulkShipmentRepository")
  private fBulkShipmentRepository!: BulkShipmentRepository;

  @Inject("ImageHandlerService")
  private fImageHandlerService!: ImageHandlerService;

  @Inject("window")
  private fWindow!: Window;

  private fIsLoading = false;

  private fBulkShipmentStore: BulkShipmentStore;
  private fFactoryDataStore: FactoryStore;
  private fFileStorageDataStore: FileStorageStore;
  private fFactoryInvoiceDataStore: FactoryInvoiceStore;

  get canExport(): boolean {
    const user = this.fAuthenticatedUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.BULK_SHIPMENTS_EXPORT);
  }

  get canDownloadManifestPdf(): boolean {
    if (this.bulkShipment?.status !== BulkShipmentStatusValue.SENT) {
      return false;
    }
    const user = this.fAuthenticatedUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.SHIPPING_MANIFESTS_READ);
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get factoryName(): string {
    if (!this.bulkShipment) {
      return "";
    }

    const factoryId = this.bulkShipment.factory;

    const factory = this.fFactoryDataStore.getFactoryById(factoryId);

    return factory ? factory.name : "";
  }

  get carrier(): string {
    return this.bulkShipment?.carrier || "";
  }

  get canManageBulkShipments(): boolean {
    const user = this.fAuthenticatedUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.BULK_SHIPMENTS_MANAGE);
  }

  get canManageBoxInfo(): boolean {
    return (
      this.canManageBulkShipments &&
      this.bulkShipment?.status !== BulkShipmentStatusValue.SENT
    );
  }

  get canPublish(): boolean {
    if (!this.bulkShipment) {
      return false;
    }

    if (this.bulkShipment.status !== BulkShipmentStatusValue.DRAFT) {
      return false;
    }

    const user = this.fAuthenticatedUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.BULK_SHIPMENTS_MANAGE);
  }

  get canShip(): boolean {
    if (!this.bulkShipment) {
      return false;
    }

    if (this.bulkShipment.status !== BulkShipmentStatusValue.PUBLISHED) {
      return false;
    }

    const user = this.fAuthenticatedUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.SHIPPING_MANIFESTS_MANAGE);
  }

  get distributionCenter(): DistributionCenter | undefined {
    const distributionCenterId = this.bulkShipment?.distributionCenter;

    if (!distributionCenterId) {
      return;
    }

    return this.fBulkShipmentStore.getDistributionCenterById(
      distributionCenterId
    );
  }

  get distributionCenterName(): string {
    if (!this.distributionCenter) {
      return "";
    }

    return this.distributionCenter.name;
  }

  get isPublished(): boolean {
    if (!this.bulkShipment) {
      return false;
    }

    return this.bulkShipment.status === BulkShipmentStatusValue.PUBLISHED;
  }

  get isSent(): boolean {
    if (!this.bulkShipment) {
      return false;
    }

    return this.bulkShipment.status === BulkShipmentStatusValue.SENT;
  }

  get invoice(): FactoryInvoice | undefined {
    if (!this.bulkShipment) {
      return undefined;
    }

    const invoice = this.fFactoryInvoiceDataStore.getFactoryInvoiceByShipmentId(
      this.bulkShipment.id
    );

    return invoice;
  }

  get shipmentDate(): string {
    if (!this.bulkShipment) {
      return "";
    }

    return this.bulkShipment.createdAt
      ? this.bulkShipment.createdAt.toLocaleDateString()
      : "";
  }

  get statusName(): string {
    if (!this.bulkShipment) {
      return "";
    }

    const status = this.fBulkShipmentStore.getStatusById(
      this.bulkShipment.status
    );

    return status ? status.name : "";
  }

  get trackingNumber(): string {
    return this.bulkShipment?.trackingNumber || "";
  }

  get bulkShipment(): BulkShipment | undefined {
    return this.fBulkShipmentStore.getBulkShipmentById(this.bulkShipmentId);
  }

  get bulkShipmentDisplayId(): string {
    if (!this.bulkShipment) {
      return "";
    }
    return (this.bulkShipment.incrementalId || this.bulkShipment.id).toString();
  }

  constructor() {
    super();

    this.fBulkShipmentStore = getModule(BulkShipmentStore, dataStore);
    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
    this.fFileStorageDataStore = getModule(FileStorageStore, dataStore);
    this.fFactoryInvoiceDataStore = getModule(FactoryInvoiceStore, dataStore);
  }

  public async downloadAirwayBill(): Promise<void> {
    if (!this.bulkShipment || !this.bulkShipment.airwayBill) {
      return;
    }

    const storageItem = this.fFileStorageDataStore.getItemById(
      this.bulkShipment.airwayBill
    );

    if (!storageItem) {
      return;
    }

    const originalUrl = this.fImageHandlerService.getOriginalImageUrl(
      storageItem.url
    );

    const response = await fetch(originalUrl);

    if (!response.ok) {
      throw new Error("Failed to download the airway bill");
    }

    const blobBody = await response.blob();

    const fileName =
      storageItem.url.split("/").pop() ||
      `airway-bill-${this.bulkShipmentId}.jpg`;

    saveAs(blobBody, fileName);
  }

  public async downloadPdf(): Promise<void> {
    const downloadUrl = await this.fBulkShipmentExportsRepository.getPdfByShipmentId(
      this.bulkShipmentId
    );

    this.fWindow.open(downloadUrl, "_blank");
  }

  public async downloadManifestPdf(): Promise<void> {
    const downloadUrl = await this.fShippingManifestExportsRepository.getPdfByBulkShipmentId(
      this.bulkShipmentId
    );

    this.fWindow.open(downloadUrl, "_blank");
  }

  public async downloadCsv(): Promise<void> {
    const response = await this.fBulkShipmentRepository.getCsvByShipmentId(
      this.bulkShipmentId
    );

    const blob = new Blob([response.data], {
      type: "text/csv",
    });

    const fileName =
      ResponseFileNameResolver.resolve(response) ||
      `shipment_${this.bulkShipmentId}.csv`;

    saveAs(blob, fileName);
  }

  public getBulkShipmentsOrder(): Promise<string[]> {
    return this.fBulkShipmentRepository.getListOfIds(
      this.query.order,
      this.query.filter
    );
  }

  public async updateTrackingNumber(value?: string): Promise<void> {
    if (!value || !this.bulkShipment) {
      return;
    }

    await this.fBulkShipmentStore.editBulkShipment({
      id: this.bulkShipment.id,
      airwayBill: this.bulkShipment.airwayBill,
      trackingNumber: value,
      carrier: this.bulkShipment.carrier,
    });
  }

  public async updateCarrier(value?: string): Promise<void> {
    if (!value || !this.bulkShipment) {
      return;
    }

    await this.fBulkShipmentStore.editBulkShipment({
      id: this.bulkShipment.id,
      airwayBill: this.bulkShipment.airwayBill,
      trackingNumber: this.bulkShipment.trackingNumber || "",
      carrier: value,
    });
  }

  public async showShippingManifestDialog(): Promise<void> {
    if (!this.bulkShipment) {
      return;
    }

    const dialog = create(CreateShippingManifestDialog);
    const isCreated = await dialog({
      factoryId: this.bulkShipment.factory,
      bulkShipmentsIds: [this.bulkShipmentId],
    });

    if (!isCreated) {
      return;
    }

    this.fIsLoading = true;

    try {
      await this.getBulkShipmentData(this.bulkShipmentId, false);
    } finally {
      this.fIsLoading = false;
    }
  }

  protected created(): void {
    void Promise.all([
      this.fFactoryDataStore.loadFactories(),
      this.fBulkShipmentStore.loadStatuses(),
      this.fBulkShipmentStore.loadDistributionCenters(),
    ]);
  }

  private async getBulkShipmentData(bulkShipmentId: string, useCache = true) {
    const [bulkShipment] = await Promise.all([
      this.fBulkShipmentStore.loaBulkShipmentById({
        id: bulkShipmentId,
        useCache,
      }),
      this.fFactoryInvoiceDataStore.loadFactoryInvoicesByShipmentIds({
        shipmentIds: [bulkShipmentId],
      }),
    ]);

    if (bulkShipment && bulkShipment.airwayBill) {
      await this.fFileStorageDataStore.loadItemsByIds([
        bulkShipment.airwayBill,
      ]);
    }
  }

  @Watch("bulkShipmentId", { immediate: true })
  private async _onBulkShipmentIdChange() {
    if (!this.bulkShipmentId) {
      return;
    }

    this.fIsLoading = true;

    try {
      await this.getBulkShipmentData(this.bulkShipmentId);
    } finally {
      this.fIsLoading = false;
    }
  }
}
